import React from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Check, ArrowRight, Bath, Droplets, Wind, ShieldCheck, Sparkles, Leaf, Euro } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useLang } from "@/context/LanguageContext";
import { LandingHeader } from "@/components/LandingHeader";
import { LandingContact } from "@/components/LandingContact";
import { Gallery } from "@/components/Gallery";
import { Footer } from "@/components/Footer";
import { SHOP_PHOTO_WIDE } from "@/i18n/translations";
import { Seo } from "@/components/Seo";
import { track } from "@/lib/analytics";

const BookBtn = ({ label, testid, onClick, variant }) => (
  <Button
    data-testid={testid}
    onClick={onClick}
    className={
      variant === "outline"
        ? "rounded-full border-2 border-bamboo bg-transparent text-bamboo-deep hover:bg-bamboo hover:text-brand-cream font-bold h-12 px-7 py-6 text-base no-a11y-underline group"
        : "rounded-full bg-bamboo hover:bg-bamboo-hover text-brand-cream font-bold h-12 px-7 py-6 text-base no-a11y-underline group"
    }
  >
    {label}
    <ArrowRight className="h-4 w-4 ml-2 group-hover:translate-x-1 transition-transform duration-200" />
  </Button>
);

export default function LandingSelf() {
  const { t } = useLang();
  const s = t.ls;
  const navigate = useNavigate();

  const goBook = (source) => {
    track("landing_cta", { fb_event: "Lead", landing: "self", source });
    navigate("/#selfwash");
  };

  return (
    <div className="bg-background overflow-x-hidden">
      <Seo title={t.seo.self_title} description={t.seo.self_desc} path="/landing/self" />
      <LandingHeader backLabel={s.back} />

      {/* Hero */}
      <section className="relative min-h-[80vh] flex items-center overflow-hidden pt-[72px]" data-testid="ls-hero">
        <div className="absolute inset-0">
          <img src={SHOP_PHOTO_WIDE} alt="Care For Me self wash" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-brand-ink/60" />
          <div className="absolute inset-0 bg-gradient-to-r from-bamboo-deep/70 to-transparent" />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-5 sm:px-8 py-16 w-full">
          <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }} className="max-w-2xl">
            <span className="inline-flex items-center gap-2 rounded-full glass-dark border border-white/20 px-4 py-1.5 text-xs font-bold uppercase tracking-[0.15em] text-stone">
              <Bath className="h-3.5 w-3.5 text-bamboo-soft" /> {s.badge}
            </span>
            <h1 className="mt-6 font-heading text-5xl sm:text-6xl font-medium leading-[1.05] text-stone tracking-tight">{s.hero_h1}</h1>
            <p className="mt-6 text-lg text-stone/85 leading-relaxed">{s.hero_p}</p>
            <div className="mt-8">
              <BookBtn label={s.cta_book} testid="ls-hero-cta" onClick={() => goBook("hero")} />
            </div>
          </motion.div>
        </div>
        <div className="absolute bottom-0 inset-x-0 h-20 bg-gradient-to-t from-background to-transparent z-10" />
      </section>

      {/* How it works */}
      <section className="py-24 sm:py-28" data-testid="ls-how">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <h2 className="font-heading text-4xl sm:text-5xl font-medium text-brand-ink tracking-tight text-center max-w-3xl mx-auto">{s.how_h2}</h2>
          <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {s.how_steps.map((step, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: i * 0.08 }}
                className="rounded-2xl bg-white border border-brand/10 p-6 shadow-[0_8px_30px_rgba(42,37,34,0.06)]"
                data-testid={`ls-step-${i}`}
              >
                <span className="grid place-items-center h-11 w-11 rounded-2xl bg-bamboo text-brand-cream font-heading text-xl font-bold mb-4">{i + 1}</span>
                <h3 className="font-heading text-xl font-semibold text-brand-ink leading-tight">{step.t.replace(/^\d+\.\s*/, "")}</h3>
                <p className="mt-2 text-sm text-brand-muted leading-relaxed">{step.d}</p>
              </motion.div>
            ))}
          </div>
          <div className="mt-10 text-center">
            <BookBtn label={s.cta_request} testid="ls-how-cta" onClick={() => goBook("how")} />
          </div>
        </div>
      </section>

      {/* What you'll find */}
      <section className="py-24 sm:py-28 bg-stone-alt/50" data-testid="ls-find">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <h2 className="font-heading text-4xl sm:text-5xl font-medium text-brand-ink tracking-tight">{s.find_h2}</h2>
          <ul className="mt-8 grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {s.find.map((item, i) => {
              const icons = [Bath, Droplets, Sparkles, Wind, Check, Leaf];
              const Icon = icons[i % icons.length];
              return (
                <motion.li
                  key={i}
                  initial={{ opacity: 0, x: 20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: i * 0.05 }}
                  className="flex items-center gap-3 rounded-2xl bg-white border border-brand/10 p-4"
                  data-testid={`ls-find-${i}`}
                >
                  <span className="grid place-items-center h-9 w-9 rounded-full bg-bamboo-soft text-bamboo-deep shrink-0"><Icon className="h-4.5 w-4.5" /></span>
                  <span className="font-semibold text-brand-ink text-sm">{item}</span>
                </motion.li>
              );
            })}
          </ul>
          <div className="mt-10">
            <BookBtn label={s.cta_request} testid="ls-find-cta" onClick={() => goBook("find")} />
          </div>
        </div>
      </section>

      {/* Why */}
      <section className="py-24 sm:py-28 bg-brand text-brand-cream" data-testid="ls-why">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <h2 className="font-heading text-4xl sm:text-5xl font-medium tracking-tight text-center">{s.why_h2}</h2>
          <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-5 gap-4">
            {s.why.map((w, i) => {
              const icons = [ShieldCheck, Sparkles, Wind, Euro, Check];
              const Icon = icons[i % icons.length];
              return (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: i * 0.06 }}
                  className="rounded-2xl bg-white/8 border border-white/12 p-5 text-center"
                  data-testid={`ls-why-${i}`}
                >
                  <span className="mx-auto grid place-items-center h-11 w-11 rounded-full bg-bamboo/25 text-bamboo-soft mb-3"><Icon className="h-5 w-5" /></span>
                  <p className="font-semibold text-sm">{w}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Space + Gallery */}
      <Gallery />

      {/* Contact + hours */}
      <LandingContact heading={s.contact_h2} />

      <Footer />
    </div>
  );
}
